import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroTrash, heroXMark } from '@ng-icons/heroicons/outline';
import { IfPermissionDirective } from '../../../../core/auth/permission.directive';
import {
  UiBadge,
  UiButton,
  UiChannelIcon,
  UiEmptyText,
  UiIconButton,
  UiPanelCard,
  UiTabButton,
  UiTabIndicator,
  UiTabList,
  UiTabPanel,
  UiTabs,
  UiTextarea,
} from '../../../../shared/ui';
import { ConversationFiltersStore, type DetailsTab } from '../../data/conversation-filters.store';
import { ConversationsStore } from '../../data/conversations.store';
import { conversationPriorityLabel } from '../../i18n/conversation-labels';
import type { ConversationView } from '../../models/conversation-view.types';
import { computeSlaBadge, type SlaTone } from '../../sla/sla-badge';
import { createNowSignal } from '../../sla/now-signal';
import { HistoryTabComponent } from '../details-panel/history-tab/history-tab';
import { provideEventRendererRegistry } from '../details-panel/history-tab/event-registry.providers';
import { LabelsPickerComponent } from '../labels-picker/labels-picker';

const DETAILS_TABS: readonly DetailsTab[] = ['details', 'notes', 'history'];
type DetailsBadgeTone = 'warning' | 'danger' | 'neutral' | 'accent';

/**
 * Body of the details panel — tabs (Geral / Notas / Histórico) and their content.
 * Extracted so both the side rail (`app-conversation-details-panel`) and the expanded
 * modal can render the exact same surface without duplicating template / state.
 */
@Component({
  selector: 'app-details-panel-content',
  imports: [
    NgIcon,
    IfPermissionDirective,
    UiBadge,
    UiButton,
    UiEmptyText,
    UiIconButton,
    UiPanelCard,
    UiTextarea,
    UiTabs,
    UiTabList,
    UiTabButton,
    UiTabIndicator,
    UiTabPanel,
    LabelsPickerComponent,
    HistoryTabComponent,
    UiChannelIcon,
    UiEmptyText,
    UiIconButton,
  ],
  providers: [
    provideIcons({ heroTrash, heroXMark }),
    ...provideEventRendererRegistry(),
  ],
  templateUrl: './details-panel-content.html',
  styleUrls: ['./details-panel-content.css', '../conversation-atoms.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsPanelContentComponent {
  protected readonly store = inject(ConversationsStore);
  protected readonly filters = inject(ConversationFiltersStore);
  protected readonly conversation = computed(() => this.store.selectedConversation());
  /** Canal (número do negócio) da conversa: provedor/nome/número/ativo. Null até a lista
   *  de canais carregar ou se o canal não for encontrado. */
  protected readonly channelSummary = computed(() => {
    const conversation = this.conversation();
    if (!conversation) return null;
    return this.store.channelSummaries().get(conversation.channelAccountId) ?? null;
  });
  protected readonly slaBadge = computed(() => {
    const conversation = this.conversation();
    if (!conversation) return null;
    return computeSlaBadge(
      {
        firstResponseAt: conversation.firstResponseAt,
        status: conversation.status,
        awaitingReplySince: conversation.awaitingReplySince ?? null,
        awaitingTrack: conversation.awaitingTrack ?? null,
        snoozedUntil: conversation.snoozedUntil,
      },
      this.store.slaPolicy(),
      // Relógio como signal: sem ele o `computed` memoizava e o selo ficava congelado no valor da
      // primeira renderização, mesmo depois de o turno estourar.
      this.now(),
    );
  });
  private readonly now = createNowSignal();
  protected readonly noteDraft = signal('');

  setTab(tab: string | undefined): void {
    const next = DETAILS_TABS.find((value) => value === tab) ?? 'details';
    this.filters.setDetailsTab(next);
  }

  setNoteDraft(value: string): void {
    this.noteDraft.set(value);
  }

  addNote(): void {
    const text = this.noteDraft().trim();
    if (text.length === 0) return;
    this.store.createNote(text);
    this.noteDraft.set('');
  }

  deleteNote(noteId: string): void {
    this.store.deleteNote(noteId);
  }

  unsnooze(): void {
    this.store.unsnooze();
  }

  protected priorityLabel(priority: ConversationView['priority']): string {
    return conversationPriorityLabel(priority);
  }

  protected slaBadgeTone(tone: SlaTone): DetailsBadgeTone {
    if (tone === 'warning' || tone === 'danger') return tone;
    if (tone === 'paused') return 'neutral';
    return 'accent';
  }
}
